import React, { useMemo, useState } from 'react';
import { Currency, formatMoney } from '../../../playground/common/format';
import { RequestResponseViewer } from '../../../playground/common/request-response-viewer';
import {
  ChartSeriesConfig,
  CurrencyHeader,
  EvolutionChart,
  KpiCard,
  KpiGrid,
  ProjectionColumn,
  ProjectionTable,
  SimulationWarnings,
  buildEvolutionSeries,
} from '../../../playground/endpoints/_shared';
import {
  FamilySavingsV2MonthlyRow,
  FamilySavingsV2Response,
  FamilySavingsV2YearlyRow,
} from './service';

const YEARLY_COLUMNS: ProjectionColumn<FamilySavingsV2YearlyRow>[] = [
  { id: 'year', label: 'Año', isMoney: false },
  { id: 'age', label: 'Edad', isMoney: false },
  { id: 'actuarial_age', label: 'Edad actuarial', isMoney: false },
  { id: 'accumulated_prime', label: 'Prima acumulada', isMoney: true },
  { id: 'coverage_costs', label: 'Costo coberturas', isMoney: true },
  { id: 'policy_expenses', label: 'Gastos póliza', isMoney: true },
  { id: 'rentability', label: 'Rentado', isMoney: true },
  { id: 'policy_value', label: 'Valor póliza', isMoney: true },
  { id: 'withdrawable_amount', label: 'Monto rescatable', isMoney: true },
  { id: 'death_compensation', label: 'Indemn. fallecimiento', isMoney: true },
];

const WITHDRAWAL_COLUMNS: ProjectionColumn<FamilySavingsV2YearlyRow>[] = [
  { id: 'withdrawal', label: 'Retiro', isMoney: true },
  { id: 'policy_value_before_withdrawal', label: 'VP pre-retiro', isMoney: true },
  { id: 'policy_value_after_withdrawal', label: 'VP post-retiro', isMoney: true },
];

const CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  { dataKey: 'accumulated_prime', name: 'Prima acumulada', stroke: 'var(--bs-gray-600)' },
  {
    dataKey: 'death_compensation',
    name: 'Indemn. fallecimiento',
    stroke: 'var(--bs-success)',
    strokeDasharray: '5 4',
  },
];

const WITHDRAWAL_CHART_SERIES: ChartSeriesConfig = {
  dataKey: 'withdrawal',
  name: 'Retiro',
  stroke: 'var(--bs-warning)',
};

/**
 * `projection_by_month` rows carry no `calendar_month` for this product, and
 * the shared `month` column renders a calendar name from it. The index is
 * republished under its own key so the monthly table can show a bare month
 * number instead of `3 — undefined`.
 */
interface FamilySavingsV2MonthlyTableRow {
  month_number: number;
  [key: string]: number | undefined;
}

function toMonthlyTableRow(row: FamilySavingsV2MonthlyRow): FamilySavingsV2MonthlyTableRow {
  // Single cast at the boundary: the spread carries `coverage_costs_breakdown`
  // through untouched -- the shared table reads it off the row to render the
  // coverage-costs popover -- even though it is not a number.
  return { ...row, month_number: row.month } as unknown as FamilySavingsV2MonthlyTableRow;
}

function buildMonthlyColumns(
  hasWithdrawals: boolean,
): ProjectionColumn<FamilySavingsV2MonthlyTableRow>[] {
  const yearly = (hasWithdrawals ? withYearlyWithdrawals(YEARLY_COLUMNS) : YEARLY_COLUMNS)
    .filter((column) => column.id !== 'year')
    .map((column) => column as unknown as ProjectionColumn<FamilySavingsV2MonthlyTableRow>);

  return [
    { id: 'month_number', label: 'Mes', isMoney: false },
    ...yearly,
  ];
}

/**
 * The withdrawal trio lands right after "Valor póliza" and before "Monto
 * rescatable", matching the column order every other playground table uses.
 */
function withYearlyWithdrawals(
  columns: ProjectionColumn<FamilySavingsV2YearlyRow>[],
): ProjectionColumn<FamilySavingsV2YearlyRow>[] {
  const splitAt = columns.findIndex((column) => column.id === 'withdrawable_amount');
  return [
    ...columns.slice(0, splitAt),
    ...WITHDRAWAL_COLUMNS,
    ...columns.slice(splitAt),
  ];
}

interface FamilySavingsV2ResultViewProps {
  request: unknown;
  response?: FamilySavingsV2Response;
  error?: unknown;
}

export function FamilySavingsV2ResultView({
  request,
  response,
  error,
}: FamilySavingsV2ResultViewProps) {
  const [currency, setCurrency] = useState<Currency>('UF');
  const hasSuccess = !!response && !error;

  // Read off the response rather than sniffed from the request: `output/
  // monthly.py` only emits the withdrawal keys when the run actually scheduled
  // one, which is the same question the columns and series need answered.
  const hasWithdrawals = useMemo(
    () => (response?.projection_by_year ?? []).some((row) => row.withdrawal !== undefined),
    [response],
  );

  const monthlyRows = useMemo(
    () => (response?.projection_by_month ?? []).map(toMonthlyTableRow),
    [response],
  );
  const yearlyColumns = useMemo(
    () => (hasWithdrawals ? withYearlyWithdrawals(YEARLY_COLUMNS) : YEARLY_COLUMNS),
    [hasWithdrawals],
  );
  const monthlyColumns = useMemo(() => buildMonthlyColumns(hasWithdrawals), [hasWithdrawals]);
  const yearlySeries = useMemo(
    () => (hasWithdrawals ? [...CHART_SERIES, WITHDRAWAL_CHART_SERIES] : CHART_SERIES),
    [hasWithdrawals],
  );
  const monthlySeries = useMemo<ChartSeriesConfig[]>(
    () => (hasWithdrawals ? [CHART_SERIES[0], WITHDRAWAL_CHART_SERIES] : [CHART_SERIES[0]]),
    [hasWithdrawals],
  );

  return (
    <div className="mt-4">
      {hasSuccess && response && (
        <>
          <CurrencyHeader
            ufValue={response.uf_value}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <SimulationWarnings warnings={response.warnings} />

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Ahorro bruto"
                value={formatMoney(response.savings.gross, currency, response.uf_value)}
                hint={`${response.timeframe_months} meses`}
                accent="text-primary"
              />
              <KpiCard
                label="Ahorro neto"
                value={formatMoney(response.savings.net, currency, response.uf_value)}
                accent="text-success"
              />
              <KpiCard
                label="Ganado por interés"
                value={formatMoney(response.savings.interest, currency, response.uf_value)}
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Prima básica"
                value={formatMoney(response.premium.basic, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima acordada"
                value={formatMoney(response.premium.agreed, currency, response.uf_value)}
                hint="Resuelta por el motor salvo en capacidad de ahorro"
              />
              <KpiCard
                label="Prima excedente"
                value={formatMoney(response.premium.excess, currency, response.uf_value)}
                accent="text-danger"
              />
            </KpiGrid>
          </div>

          {(response.coverage_factor !== undefined
            || response.terminal_excess !== undefined
            || response.objective_achieved_month !== undefined) && (
            <div className="mb-4">
              <h6 className="text-muted text-uppercase small">Objetivo</h6>
              <KpiGrid>
                {response.coverage_factor !== undefined && (
                  <KpiCard
                    label="Cobertura del objetivo"
                    value={`${(response.coverage_factor * 100).toFixed(1)}%`}
                    hint="Fracción del objetivo que financia la prima acordada"
                    accent={response.coverage_factor < 1 ? 'text-warning' : 'text-success'}
                  />
                )}
                {response.funded_goal !== undefined && (
                  <KpiCard
                    label="Objetivo financiado"
                    value={formatMoney(response.funded_goal, currency, response.uf_value)}
                    hint={`Sobre un objetivo de ${formatMoney(response.goal, currency, response.uf_value)}`}
                  />
                )}
                {response.terminal_excess !== undefined && (
                  <KpiCard
                    label="Excedente terminal"
                    value={formatMoney(response.terminal_excess, currency, response.uf_value)}
                    hint="Saldo del valor póliza tras el último retiro"
                    accent="text-info"
                  />
                )}
                {response.objective_achieved_month !== undefined && (
                  <KpiCard
                    label="Mes del objetivo"
                    value={String(response.objective_achieved_month)}
                    hint="Mes del último retiro que el motor debía financiar"
                  />
                )}
              </KpiGrid>
            </div>
          )}

          <div className="mb-4">
            <EvolutionChart
              data={buildEvolutionSeries(
                response.projection_by_year,
                yearlySeries.map((series) => series.dataKey),
                currency,
                response.uf_value,
              )}
              series={yearlySeries}
              currency={currency}
            />
          </div>

          {monthlyRows.length > 0 && (
            <>
              <div className="mb-4">
                <EvolutionChart
                  title="Evolución mensual de la póliza"
                  data={buildEvolutionSeries(
                    monthlyRows,
                    monthlySeries.map((series) => series.dataKey),
                    currency,
                    response.uf_value,
                    ['month'],
                  )}
                  series={monthlySeries}
                  currency={currency}
                  xKey="month"
                  xLabel="Mes"
                />
              </div>

              <div className="mb-4">
                <ProjectionTable
                  rows={monthlyRows}
                  columns={monthlyColumns}
                  currency={currency}
                  ufValue={response.uf_value}
                  title="Detalle mensual"
                  rowKey="month_number"
                />
              </div>
            </>
          )}

          <div className="mb-4">
            <ProjectionTable
              rows={response.projection_by_year}
              columns={yearlyColumns}
              currency={currency}
              ufValue={response.uf_value}
            />
          </div>
        </>
      )}

      <RequestResponseViewer request={request} response={response} error={error} />
    </div>
  );
}
